import { createAppStateStore } from '@erp/json-renderer/apps/react';
import type {
  AppActionHandler,
  AppStateStore,
} from '@erp/json-renderer/apps/react';
import type { AppCommandResult, OpenedApp } from '@erp/rpc';
import type {
  AppInvocationCancellation,
  AppInvocationFailure,
  AppInvocationRejection,
} from '@erp/shared-types';
import { commandStatusPatch, feedStatusPatch } from './appState';
import { commandResponse, reconcileCommand } from './appCommandOutcome';
import { boundedText, summarize } from './appInspection';
import { APP_DIAGNOSTIC_LIMIT, createAppDiagnostics } from './appDiagnostics';
import type { AppDomainEvent, AppTransition } from './appInvocationLifecycle';

type CommandRequest = {
  command: string;
  params: Record<string, unknown>;
  deploymentId: string;
};

/** Every way a consumer's invocation can end. Exported once a host drives it. */
type AppActionErrorReason =
  AppInvocationRejection | AppInvocationFailure | 'canceled';

/** The behavior host throws this, so the renderer and trace agree on a reason. */
export class AppActionError extends Error {
  readonly reason: AppActionErrorReason;
  constructor({
    reason,
    message = reason,
  }: {
    reason: AppActionErrorReason;
    message?: string;
  }) {
    super(message);
    this.name = 'AppActionError';
    this.reason = reason;
  }
}

/**
 * A consumer of one behavior context: an open editor, or any other owner that
 * can be replaced while its write is still in flight. Retiring it settles its
 * invocation as canceled; it never releases the App's mutation gate, because a
 * write already accepted by the server still has to finish.
 */
export interface AppInvocationSession {
  readonly id: number;
  isLive(): boolean;
  retire(): void;
}

/** How many replaceable consumers one App may keep live at once. */
const MAX_LIVE_SESSIONS = 32;

interface SessionState {
  readonly id: number;
  readonly lease: number;
  retired: boolean;
}

interface ActiveInvocation {
  readonly id: number;
  readonly command: string;
  readonly sessionId: number | null;
  canceled: boolean;
  settleCanceled(): void;
}

/**
 * One mutation (including its server-side feed refresh) at a time per open App.
 * Overlaps are rejected, never queued or retried. Disposal ignores UI outcomes;
 * it does not cancel or roll back a mutation already accepted by the server.
 * Rejections/failures throw so json-render runs onError, never onSuccess.
 * A successful write with refresh errors still resolves; the write took effect.
 */
export function createAppController(options: {
  opened: OpenedApp;
  run: (request: CommandRequest) => Promise<AppCommandResult>;
  notify: (message: string) => void;
  busyMessage: string;
  /** Transitions kept for the inspector and `read_app_live_state`. */
  diagnosticLimit?: number;
}) {
  const { opened } = options;
  const identity = {
    appId: opened.app.id,
    deploymentId: opened.deployment.id,
    instanceId: crypto.randomUUID(),
  };
  const source = createAppStateStore({ ...opened.view.state, ...opened.state });
  source.update(feedStatusPatch(opened.state, opened.errors));
  const diagnostics = createAppDiagnostics(options.diagnosticLimit);
  let feedErrors = opened.errors;
  let disposed = false;
  let generation = 0;
  let active: ActiveInvocation | null = null;
  let invocation = 0;
  let sequence = 0;
  let sessionSequence = 0;
  const sessions = new Map<number, SessionState>();

  function record<E extends AppDomainEvent>(
    sessionId: number | null,
    detail: E,
  ): void {
    // A union member plus the transition context is exactly a transition;
    // TypeScript cannot prove that through a spread over a generic union.
    diagnostics.record(
      Object.assign(
        { ...identity, sessionId, sequence: ++sequence, at: Date.now() },
        detail,
      ) as AppTransition,
    );
  }

  record(null, { kind: 'opened' });

  // Wrap the synchronous boundary itself: external-store mode bypasses
  // onStateChange. Internal lifecycle writes use source and emit domain events.
  const store: AppStateStore = {
    ...source,
    set(path, value) {
      if (disposed) return;
      record(null, {
        kind: 'bound-write',
        changes: summarize({ [path]: value }, 1200),
      });
      source.set(path, value);
    },
    update(changes) {
      if (disposed) return;
      record(null, { kind: 'bound-write', changes: summarize(changes, 1200) });
      source.update(changes);
    },
  };

  function openSession(): AppInvocationSession {
    if (disposed) {
      const dead: AppInvocationSession = {
        id: 0,
        isLive: () => false,
        retire: () => {},
      };
      return dead;
    }
    // Bound live ownership: the oldest consumer gives up its claim first.
    if (sessions.size >= MAX_LIVE_SESSIONS) {
      const oldest = sessions.values().next().value;
      if (oldest) retireSession(oldest);
    }
    const session: SessionState = {
      id: ++sessionSequence,
      lease: generation,
      retired: false,
    };
    sessions.set(session.id, session);
    return {
      id: session.id,
      isLive: () => sessionLive(session),
      retire: () => retireSession(session),
    };
  }

  function sessionLive(session: SessionState): boolean {
    return !disposed && generation === session.lease && !session.retired;
  }

  function sessionUsable(sessionId: number): boolean {
    const session = sessions.get(sessionId);
    return session !== undefined && sessionLive(session);
  }

  function retireSession(session: SessionState): void {
    if (session.retired) return;
    session.retired = true;
    sessions.delete(session.id);
    const current = active;
    if (current?.sessionId === session.id)
      cancelActive(current, 'owner-retired');
  }

  function cancelActive(
    ticket: ActiveInvocation,
    reason: AppInvocationCancellation,
  ): void {
    if (ticket.canceled) return;
    ticket.canceled = true;
    record(ticket.sessionId, {
      kind: 'action-canceled',
      invocationId: ticket.id,
      command: boundedText(ticket.command, 200),
      reason,
      remoteMayComplete: true,
    });
    ticket.settleCanceled();
  }

  function request(
    command: string,
    params: Record<string, unknown>,
    context: {
      sessionId?: number;
      /** Receives the invocation id before anything runs; used for causal traces. */
      onInvocation?: (invocationId: number) => void;
    } = {},
  ): Promise<unknown> {
    const invocationId = ++invocation;
    const sessionId = context.sessionId ?? null;
    const rejection = rejectionReason(command, sessionId);
    if (rejection) {
      record(sessionId, {
        kind: 'action-requested',
        invocationId,
        command: boundedText(command, 200),
        params: summarize(params, 1200),
      });
      const message = rejection === 'busy' ? options.busyMessage : rejection;
      record(sessionId, {
        kind: 'action-rejected',
        invocationId,
        command: boundedText(command, 200),
        reason: rejection,
        message,
      });
      if (rejection === 'busy') options.notify(options.busyMessage);
      return Promise.reject(new AppActionError({ reason: rejection, message }));
    }

    const startedGeneration = generation;
    let settled = false;
    let resolveConsumer: ((value: unknown) => void) | null = null;
    let rejectConsumer: ((error: unknown) => void) | null = null;
    // The consumer exists before any subscriber can observe the request, so a
    // reentrant retirement can always settle it instead of leaving it pending.
    const consumer = new Promise<unknown>((resolve, reject) => {
      resolveConsumer = resolve;
      rejectConsumer = reject;
    });
    const ticket: ActiveInvocation = {
      id: invocationId,
      command,
      sessionId,
      canceled: false,
      settleCanceled() {
        if (settled) return;
        settled = true;
        rejectConsumer?.(new AppActionError({ reason: 'canceled' }));
      },
    };
    // Ownership is registered before any subscriber can observe the request.
    active = ticket;
    context.onInvocation?.(invocationId);
    record(sessionId, {
      kind: 'action-requested',
      invocationId,
      command: boundedText(command, 200),
      params: summarize(params, 1200),
    });
    record(sessionId, {
      kind: 'command-started',
      invocationId,
      command: boundedText(command, 200),
    });
    source.update(
      commandStatusPatch(command, { running: true, result: null, error: null }),
    );

    const owns = () =>
      !ticket.canceled &&
      !disposed &&
      generation === startedGeneration &&
      active?.id === invocationId;

    void execute().then(
      (value) => {
        if (settled) return;
        settled = true;
        resolveConsumer?.(value);
      },
      (error: unknown) => {
        if (settled) return;
        settled = true;
        rejectConsumer?.(error);
      },
    );
    return consumer;

    async function execute(): Promise<unknown> {
      try {
        requireLiveGeneration(startedGeneration);
        if (ticket.canceled) {
          // Canceled before the transport started: nothing is in flight, so
          // the gate can be released without waiting for a write.
          release(invocationId);
          return undefined;
        }
        const response = await commandResponse(() =>
          options.run({
            command,
            params,
            deploymentId: identity.deploymentId,
          }),
        );
        if (ticket.canceled) {
          // The consumer already learned it was canceled; this result is stale.
          forgetLateResult(ticket);
          return undefined;
        }
        if (!owns()) throw new AppActionError({ reason: 'disposed' });
        const settledOutcome = reconcileCommand({
          command,
          response,
          currentErrors: feedErrors,
        });
        feedErrors = settledOutcome.feedErrors;
        record(sessionId, {
          kind: 'command-settled',
          invocationId,
          command: boundedText(command, 200),
          outcome: settledOutcome.outcome,
          summary: summarize(settledOutcome.summary, 1200),
          refreshWarnings: settledOutcome.refreshWarnings,
          message: settledOutcome.error,
        });
        // Publish every accepted change before notifying or resolving.
        source.update(settledOutcome.patch);
        requireLiveGeneration(startedGeneration);
        settledOutcome.notices.forEach(options.notify);
        requireLiveGeneration(startedGeneration);
        release(invocationId);
        switch (settledOutcome.outcome) {
          case 'success':
          case 'refresh':
            return settledOutcome.result;
          default:
            throw new AppActionError({
              reason: settledOutcome.outcome,
              message: settledOutcome.error ?? settledOutcome.outcome,
            });
        }
      } catch (error) {
        release(invocationId);
        throw error;
      }
    }
  }

  /** Give up ownership only when it is still this invocation's to give up. */
  function release(invocationId: number): void {
    if (active?.id === invocationId) active = null;
  }

  function forgetLateResult(ticket: ActiveInvocation): void {
    if (!disposed) {
      record(ticket.sessionId, {
        kind: 'result-ignored',
        invocationId: ticket.id,
        command: boundedText(ticket.command, 200),
        reason: 'late-result',
        remoteMayComplete: true,
      });
      // The consumer is gone, so nothing else will clear its running status.
      // Only while this invocation still owns the gate: a newer one may have
      // already published its own status for the same command.
      if (active?.id === ticket.id)
        source.update(
          commandStatusPatch(ticket.command, {
            running: false,
            result: null,
            error: null,
          }),
        );
    }
    release(ticket.id);
  }

  function requireLiveGeneration(expected: number) {
    if (disposed || generation !== expected)
      throw new AppActionError({ reason: 'disposed' });
  }

  function rejectionReason(
    command: string,
    sessionId: number | null,
  ): AppInvocationRejection | null {
    if (disposed) return 'disposed';
    if (sessionId !== null && !sessionUsable(sessionId)) return 'disposed';
    if (!opened.commands.includes(command)) return 'unknown-command';
    return active !== null ? 'busy' : null;
  }

  const handlers: Record<string, AppActionHandler> = Object.fromEntries(
    opened.commands.map((command) => [
      command,
      (params: Record<string, unknown>) => request(command, params),
    ]),
  );
  return {
    identity,
    store,
    handlers,
    request,
    openSession,
    diagnostics: {
      subscribe: (listener: () => void) => diagnostics.subscribe(listener),
      getSnapshot: () => diagnostics.getSnapshot(),
    },
    getFeedErrors: () => feedErrors,
    /**
     * Publish state the host owns (behavior instances), bypassing the view's
     * write wrapper so it is not reported as a view write.
     */
    publish(changes: Record<string, unknown>) {
      if (disposed) return;
      source.update(changes);
    },
    inspect(eventLimit = 20) {
      const state = source.getSnapshot();
      const feeds = new Set(Object.keys(opened.state));
      const inputs = Object.fromEntries(
        Object.entries(state).filter(
          ([key]) =>
            !feeds.has(key) &&
            !['$data', 'commands', 'selectedRow', 'selectedRowAction'].includes(
              key,
            ),
        ),
      );
      const snapshot = diagnostics.getSnapshot();
      return {
        ...identity,
        disposed,
        activeInvocationId: active?.id ?? null,
        activeSessionId: active?.sessionId ?? null,
        liveSessions: [...sessions.keys()],
        inputs: summarize(inputs, 6000),
        selection: {
          row: summarize(state.selectedRow ?? null, 3000),
          action: summarize(state.selectedRowAction ?? null, 400),
        },
        commands: summarize(state.commands, 3000),
        feedStatus: summarize(state.$data, 2000),
        feedErrors: summarize(feedErrors, 2000),
        events: snapshot.events
          .slice(-Math.max(1, Math.min(APP_DIAGNOSTIC_LIMIT, eventLimit)))
          .map((event) => ({ ...event })),
        diagnostics: {
          revision: snapshot.revision,
          dropped: snapshot.dropped,
        },
        limits: {
          events: APP_DIAGNOSTIC_LIMIT,
          valueSummaryCharacters: 1200,
          snapshotsTruncated: true,
        },
      };
    },
    // React StrictMode can clean up and reconnect the same mounted store.
    connect() {
      disposed = false;
      const lease = ++generation;
      return () => {
        if (generation !== lease) return;
        disposed = true;
        generation++;
        active = null;
        for (const session of sessions.values()) session.retired = true;
        sessions.clear();
        record(null, { kind: 'disposed' });
      };
    },
  };
}
export type AppController = ReturnType<typeof createAppController>;
